import React from 'react'
import {
    Row,
    Col, Label, Input, Button,
    Table,
} from 'reactstrap'
import {getAccounts, updateAccount, deleteAccount} from '../../model/request/admin'
import {
    getManageableEmployees,
    setAsanteRole,
    setCustomerRole,
    setVerified,
    setCompany,
} from '../../model/request/employee_verification'
import {Account, Roles, Permissions} from '../../model/Account'
import {AlertModalModel} from '../modal/AlertModalController'
import {InfoButton} from '../../view/InfoButton'

const VERIFIED_DESCRIPTION = 'Confirms this is a real person and that their ' +
    'account information (like company name) is correct. Unverified accounts ' +
    'can only make quotes until someone verifies them.'

const ROLE_DESCRIPTIONS = {
    'Customer': 'For customer company contacts. Their company name must match ' +
        'a real company already in the system - it gives them access to view ' +
        'all quotes for that company.',
    'Asante': 'For all Asante employees. Over 90% of employees only need this ' +
        'role plus Verified.',
    'Admin': 'Full administrative access, including editing any account and ' +
        'every other role/permission. Reserve for owners/managers.',
    'PM': 'Currently only used to populate a project-manager dropdown in a ' +
        'few places on the site. Should only be given to actual project managers.',
    'Quoter': 'For senior quoters you want to be able to edit pricing and ' +
        'modify addons.',
    'Machine': 'For anyone who should see our machine data - CNC router data ' +
        'and environment data in a few places around the building.',
    'SelectQuery': 'Direct custom-SQL access to the database. Should only go ' +
        'to a small, trusted set of individuals (owners/upper management) for ' +
        'security reasons - they can see and run all custom queries that are made.',
    'AsanteAdmin': 'A smaller admin role for trusted Asante employees who ' +
        'need to verify new signups and grant/revoke the Asante role, without ' +
        'full Admin\'s access to everything else on the site.',
}

class AdminAccountsViewController extends React.Component {

    constructor(props) {
        super(props)
        this.account = Account.fromCookieAndLocalStorage()
        this.isFullAdmin = !!this.account && this.account.hasRole(Roles.Admin)
        this.state = {
            'accounts': [],
            'pendingChanges': {},
        }
    }

    sortAccounts(accounts) {
        return accounts.sort((l, r) => {
            if (l.verified !== r.verified) {
                return r.verified - l.verified
            }
            if (l.role !== r.role) {
                return r.role - l.role
            }
            if (l.permissions !== r.permissions) {
                return r.permissions - l.permissions
            }
            if (l.company.localeCompare(r.company) !== 0) {
                return l.company.localeCompare(r.company)
            }
            if (l.email.localeCompare(r.email) !== 0) {
                return l.email.localeCompare(r.email)
            }
            return 0
        })
    }

    fetchManageableEmployees() {
        this.props.turnOnLoader()
        getManageableEmployees()
        .then(
            (result) => {
                let accounts = result.data.accounts.map((a) => Account.from(a))
                accounts = this.sortAccounts(accounts)
                this.setState({
                    'accounts': accounts,
                })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    mergePendingChange(id, fields) {
        const pendingChanges = this.state.pendingChanges
        pendingChanges[id] = {
            ...pendingChanges[id],
            ...fields,
        }
        this.setState({
            'pendingChanges': pendingChanges,
        })
    }

    componentDidMount() {
        if (!this.isFullAdmin) {
            if (this.account && ((this.account.hasRole(Roles.Customer) && !this.account.hasRole(Roles.Asante)) ||
                this.account.hasRole(Roles.AsanteAdmin))) {
                this.fetchManageableEmployees()
            }
            return
        }

        this.props.turnOnLoader()
        getAccounts()
        .then(
            (result) => {
                let accounts = result.data.accounts.map((a) => Account.from(a))
                accounts = this.sortAccounts(accounts)

                this.setState({
                    'accounts': accounts,
                })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    setRole(setRoleFn, id, grant) {
        this.props.turnOnLoader()
        setRoleFn(id, grant)
        .then(
            (result) => {
                this.props.turnOffLoader()
                this.fetchManageableEmployees()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    updateCompany(account) {
        const fields = this.state.pendingChanges[account.id]
        if (!fields || fields.company === undefined) {
            return
        }

        const pendingChanges = this.state.pendingChanges
        delete pendingChanges[account.id]
        this.setState({
            'pendingChanges': pendingChanges,
        })

        this.setRole(setCompany, account.id, fields.company)
    }

    updateAccount(account) {
        const fields = this.state.pendingChanges[account.id]
        if (!fields) {
            return
        }

        this.props.turnOnLoader()
        updateAccount(account.id, fields)
        .then(
            (result) => {
                const pendingChanges = this.state.pendingChanges
                delete pendingChanges[account.id]
                this.setState({
                    'pendingChanges': pendingChanges,
                })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    deleteAccount(account) {
        this.props.showAlertModal(
            (new AlertModalModel())
                .setTitle('Warning')
                .setMessage('Are you sure you want to delete ' + account.email + '. ' +
                    'This action can not be undone.')
                .addYesButton((e) => {
                    this.props.turnOnLoader()
                    deleteAccount(account.id).then(
                        (result) => {
                            const accounts = this.state.accounts.filter(
                                (a) => a.id !== account.id)
                            this.setState({
                                'accounts': accounts,
                            })
                            this.props.turnOffLoader()
                        },
                        (error) => {
                            this.props.showError(error)
                            this.props.turnOffLoader()
                        }
                    )
                    this.props.closeModal()
                })
                .addCancelButton((e) => {this.props.closeModal()})
        )
    }


    // MARK: Render

    renderRolePermissions(account, options, getValue, onUpdate) {
        let header = []

        for (let key in options) {
            if (options[key] === 0) {
                continue
            }

            const value = getValue(key, options[key])

            header.push(
                <td>
                    <Input type="checkbox"
                        style={{
                            "position": "relative",
                            "transform": "translate(-50%, -50%)",
                            "top": "50%",
                            "left": "50%",
                        }} checked={value}
                        onChange={(e) => {
                            onUpdate(key, options[key], !value)
                        }} />
                </td>
            )
        }

        return header
    }

    renderAccounts() {
        return this.state.accounts.map((account, index) => {
            return <tr>
                <td><Label>{account.first_name} {account.last_name}</Label></td>
                <td><Label>{account.email}</Label></td>
                <td><Input
                    value={account.company}
                    onChange={(e) => {
                        const accounts = this.state.accounts
                        accounts[index].company = e.target.value
                        this.setState({
                            'accounts': accounts,
                        })
                        this.mergePendingChange(account.id, {'company': e.target.value})
                    }} /></td>
                <td><Input type="checkbox"
                    style={{
                        "position": "relative",
                        "transform": "translate(-50%, -50%)",
                        "top": "50%",
                        "left": "50%",
                    }}
                    checked={account.verified}
                    onChange={(e) => {
                        const accounts = this.state.accounts
                        accounts[index].verified = !accounts[index].verified
                        this.setState({
                            'accounts': accounts,
                        })
                        this.mergePendingChange(account.id, {'verified': accounts[index].verified})
                    }}
                    /></td>
                {
                    this.renderRolePermissions(account, Roles,
                    (key, value) => account.hasRole(value),
                    (key, value, newValue) => {
                        if (newValue) {
                            account.addRole(value)
                        } else {
                            account.removeRole(value)
                        }
                        const accounts = this.state.accounts
                        accounts[index] = account
                        this.setState({
                            'accounts': accounts,
                        })
                        this.mergePendingChange(account.id, {'role': account.role})
                    })
                }
                {
                    this.renderRolePermissions(account, Permissions,
                    (key, value) => account.hasPermission(value),
                    (key, value, newValue) => {
                        if (newValue) {
                            account.addPermission(value)
                        } else {
                            account.removePermission(value)
                        }
                        const accounts = this.state.accounts
                        accounts[index] = account
                        this.setState({
                            'accounts': accounts,
                        })
                        this.mergePendingChange(account.id, {'permissions': account.permissions})
                    })
                }
                <td><Button color="success"
                    disabled={!this.state.pendingChanges[account.id]}
                    onClick={(e) =>
                        this.updateAccount(account)
                    } >Save</Button></td>
                <td><Button color="danger"
                    disabled={account.verified || account.role > 0 ||
                        account.permissions > 0}
                    onClick={(e) =>
                        this.deleteAccount(account)
                    } >Delete</Button></td>
            </tr>
        })
    }

    renderEmployeeAccounts() {
        const canManageAsante = this.account.hasRole(Roles.AsanteAdmin)

        return this.state.accounts.map((account, index) => {
            const canManageCustomer = canManageAsante || account.company === this.account.company

            return <tr>
                <td><Label>{account.first_name} {account.last_name}</Label></td>
                <td><Label>{account.email}</Label></td>
                <td>{canManageAsante ? <div style={{'display': 'flex', 'gap': '4px'}}>
                    <Input
                        value={account.company}
                        onChange={(e) => {
                            const accounts = this.state.accounts
                            accounts[index].company = e.target.value
                            this.setState({
                                'accounts': accounts,
                            })
                            this.mergePendingChange(account.id, {'company': e.target.value})
                        }} />
                    <Button color="success" size="sm"
                        style={{'minWidth': '140px'}}
                        disabled={!this.state.pendingChanges[account.id] ||
                            this.state.pendingChanges[account.id].company === undefined}
                        onClick={(e) =>
                            this.updateCompany(account)
                        } >Update Company</Button>
                </div> : <Label>{account.company}</Label>}</td>
                <td className="text-center"><Button
                    color={account.verified ? 'danger' : 'success'}
                    onClick={(e) =>
                        this.setRole(setVerified, account.id, !account.verified)
                    }>{account.verified ? 'Unverify' : 'Verify'}</Button></td>
                {canManageAsante && <td className="text-center">
                    <Button
                        color={account.hasRole(Roles.Asante) ? 'danger' : 'success'}
                        onClick={(e) =>
                            this.setRole(setAsanteRole, account.id, !account.hasRole(Roles.Asante))
                        }>{account.hasRole(Roles.Asante) ? 'Revoke' : 'Grant'} Asante</Button>
                </td>}
                <td className="text-center">
                    {canManageCustomer && <Button
                        color={account.hasRole(Roles.Customer) ? 'danger' : 'success'}
                        onClick={(e) =>
                            this.setRole(setCustomerRole, account.id, !account.hasRole(Roles.Customer))
                        }>{account.hasRole(Roles.Customer) ? 'Revoke' : 'Grant'} Customer</Button>}
                </td>
            </tr>
        })
    }

    renderHeaders(headerLabel, options, descriptions) {
        let header = []

        for (let key in options) {
            if (options[key] === 0) {
                continue
            }

            header.push(
                <th className="text-center label-weight"
                    style={{'minWidth': '200px'}}>{headerLabel} {key}
                    {descriptions && descriptions[key] && <InfoButton
                        id={'adminaccounts' + headerLabel + key}
                        style={{'margin': '4px'}}
                        message={descriptions[key]}
                        />}
                </th>
            )
        }

        return header
    }

    renderVerifiedHeader() {
        return <th className="text-center label-weight"
            style={{'minWidth': '200px'}}>Verified
            <InfoButton
                id={'adminaccountsverified'}
                style={{'margin': '4px'}}
                message={VERIFIED_DESCRIPTION}
                />
        </th>
    }

    renderAdminHeaders() {
        return <tr>
            <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Name</th>
            <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Email</th>
            <th className="text-center label-weight"
                style={{'minWidth': '300px'}}>Company</th>
            {this.renderVerifiedHeader()}
            {this.renderHeaders('Roles', Roles, ROLE_DESCRIPTIONS)}
            {this.renderHeaders('Permissions', Permissions)}
            <th></th>
            <th></th>
        </tr>
    }

    renderEmployeeHeaders() {
        return <tr>
            <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Name</th>
            <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Email</th>
            <th className="text-center label-weight"
                style={{'minWidth': '300px'}}>Company</th>
            {this.renderVerifiedHeader()}
            {this.account.hasRole(Roles.AsanteAdmin) && <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Asante
                <InfoButton
                    id={'adminaccountsemployeeasante'}
                    style={{'margin': '4px'}}
                    message={ROLE_DESCRIPTIONS['Asante']}
                    />
            </th>}
            <th className="text-center label-weight"
                style={{'minWidth': '200px'}}>Customer
                <InfoButton
                    id={'adminaccountsemployeecustomer'}
                    style={{'margin': '4px'}}
                    message={ROLE_DESCRIPTIONS['Customer']}
                    />
            </th>
        </tr>
    }

    render() {
        return (
            <div style={{
                "width": "100%",
                "paddingRight": "15px",
                "paddingLeft": "15px",
                "marginRight": "auto",
                "marginLeft": "auto",
            }}> <Row><Col><div style={{'overflowX': 'auto'}}><Table>
                <thead>
                    {this.isFullAdmin ? this.renderAdminHeaders() : this.renderEmployeeHeaders()}
                </thead>
                <tbody>
                    {this.isFullAdmin ? this.renderAccounts() : this.renderEmployeeAccounts()}
                </tbody>
                </Table></div></Col></Row>
            </div>
        )
    }
}

export default AdminAccountsViewController

